import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store';
import { saveTableAsTemplate } from '../../store/actions/BatchTables/BatchTablesActions';
import { hideSidePanel } from '../../store/actions/sidePanelActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import Input from '../shared/TailwindComponents/Input';
import Textarea from '../shared/TailwindComponents/Textarea';
import Button from '../shared/TailwindComponents/Button';
import { PiFloppyDiskLight } from 'react-icons/pi';

interface SaveTableTemplatePanelProps {
  tableUuid: string;
  defaultName: string;
  onClose: () => void;
}

const SaveTableTemplatePanel: React.FC<SaveTableTemplatePanelProps> = ({ tableUuid, defaultName, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [templateName, setTemplateName] = useState(defaultName);
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (!templateName.trim() || !tableUuid) return;

    setIsSaving(true);
    try {
      await dispatch(saveTableAsTemplate(tableUuid, {
        name: templateName.trim(),
        description: description.trim() || undefined,
      }) as any);
      dispatch(hideSidePanel());
    } catch (error) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to save template'),
        modalType: 'error',
        duration: 5000,
      }));
    } finally {
      setIsSaving(false);
    }
  }, [dispatch, tableUuid, templateName, description, t]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('Save the current table columns as a reusable template. You can use this template to quickly create new tables with the same structure.')}
        </p>
        <Input
          type="text"
          id="templateName"
          label={t('Template Name')}
          placeholder={t('Enter template name')}
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          required
          autoFocus
        />
        <Textarea
          id="templateDescription"
          label={t('Description')}
          placeholder={t('Describe what this template is for...')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
        />
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end gap-3">
        <Button
          variant="outline"
          size="small"
          onClick={onClose}
          disabled={isSaving}
        >
          {t('Cancel')}
        </Button>
        <Button
          variant="primary"
          size="small"
          onClick={handleSubmit}
          disabled={!templateName.trim() || isSaving}
          icon={<PiFloppyDiskLight className="w-4 h-4" />}
        >
          {isSaving ? t('Saving...') : t('Save Template')}
        </Button>
      </div>
    </div>
  );
};

export default SaveTableTemplatePanel;
